import React, { useState, useEffect } from 'react';
import { Container, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, CircularProgress, Box, Button, Pagination, TextField, InputAdornment, FormControl, Select, MenuItem } from '@mui/material';
import { Download, Search, FilterList } from '@mui/icons-material';
import { api } from '../context/AuthContext';

const planMap = {
  FREE_TRIAL: "Free Trial",
  free_trial: "Free Trial",
  PAY_AS_YOU_GO: "Pay As You Go",
  MONTHLY: "Starter",
  monthly: "Starter",           // Add this
  PROFESSIONAL: "Professional",
  professional: "Professional", // Add this
  ANNUAL: "Annual",             // Add this
  annual: "Annual",             // Add this
  OVERAGE_JOB: "Overage",
  overage_job: "Overage"        // Add this
};

function Payment() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [planFilter, setPlanFilter] = useState('All');
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  useEffect(() => {
    async function fetchPayments() {
      try {
        const endpoint = window.location.pathname.includes('super-admin')
          ? `/payment/super-admin/payments`
          : `/payment/history`;
        const response = await api.get(endpoint);
        if (Array.isArray(response.data)) {
          setPayments(response.data);
        } else {
          console.error('Expected array for payments, got:', response.data);
          setPayments([]);
        }
      } catch (error) {
        console.error('Failed to fetch payments:', error);
        setPayments([]);
      } finally {
        setLoading(false);
      }
    }
    fetchPayments();
  }, []);

const getDisplayStatus = (payment) => {
  const storedStatus = String(payment.status || '').toLowerCase();
  if (storedStatus === 'expired') {
    return 'Expired';
  }

  const plan = String(payment.plan_code || '').toUpperCase();

  if (plan === 'FREE_TRIAL') {
    const expiredByCount = Number(payment.free_trial_used_count || 0) >= 5;

    const expiredByDate =
      payment.free_trial_expires_at &&
      new Date(payment.free_trial_expires_at) < new Date();

    return expiredByCount || expiredByDate ? 'Expired' : 'Active';
  }

  if (plan === 'PAY_AS_YOU_GO') {
    const jobsPurchased = Number(payment.payg_jobs_purchased || 0);
    const jobsUsed = Number(payment.payg_jobs_used || 0);

    const totalSlots = jobsPurchased * 5;
    const usedSlots = Number(payment.payg_shortlists_used || 0);

    const expiredByUsage =
      jobsUsed >= jobsPurchased &&
      usedSlots >= totalSlots;

    const expiredByDate =
      payment.payg_expires_at &&
      new Date(payment.payg_expires_at) < new Date();

    return (expiredByUsage || expiredByDate)
      ? 'Expired'
      : 'Active';
  }
if (plan === 'OVERAGE_JOB') {
  const userPlan = String(payment.current_user_plan || '').toLowerCase();

  // Starter overage
  if (userPlan === 'monthly') {
    const jobsUsed = Number(payment.starter_overage_jobs_used || 0);
    const jobLimit = Number(payment.starter_overage_job_limit || 1);

    const shortlistUsed = Number(payment.starter_overage_shortlists_used || 0);
    const shortlistLimit = Number(payment.starter_overage_shortlist_limit || 5);

    const expiredByUsage =
      jobsUsed >= jobLimit &&
      shortlistUsed >= shortlistLimit;

    const expiredByDate =
      payment.starter_overage_expires_at &&
      new Date(payment.starter_overage_expires_at) < new Date();

    return (expiredByUsage || expiredByDate) ? 'Expired' : 'Active';
  }

  // Keep existing behavior for other overage types
  if (userPlan === 'professional' || userPlan === 'annual') {
    const remaining = Number(payment.professional_overage_jobs || 0);

    const expiryOk =
      !payment.professional_overage_expires_at ||
      new Date(payment.professional_overage_expires_at) > new Date();

    return remaining > 0 && expiryOk ? 'Active' : 'Expired';
  }

  return Number(payment.overage_jobs || 0) > 0 ? 'Active' : 'Expired';
}

  if (plan === 'MONTHLY' || plan === 'STARTER') {
    const jobsUsed = Number(payment.starter_jobs_used || 0);
    const jobLimit = Number(payment.starter_job_limit || 10);

    const shortlistUsed = Number(payment.starter_shortlists_used || 0);
    const shortlistLimit = Number(payment.starter_shortlist_limit || 50);

    const expiredByUsage =
      jobsUsed >= jobLimit &&
      shortlistUsed >= shortlistLimit;

    const expiredByDate =
      payment.expires_at &&
      new Date(payment.expires_at) < new Date();

    return (expiredByUsage || expiredByDate) ? 'Expired' : 'Active';
  }

  if (plan === 'PROFESSIONAL' || plan === 'ANNUAL') {
    const jobsUsed = Number(payment.professional_jobs_used || 0);
    const jobLimit = Number(payment.professional_job_limit || 50);

    const shortlistUsed = Number(payment.professional_shortlists_used || 0);
    const shortlistLimit = Number(payment.professional_shortlist_limit || 300);

    const expiredByUsage =
      jobsUsed >= jobLimit &&
      shortlistUsed >= shortlistLimit;

    const expiredByDate =
      payment.expires_at &&
      new Date(payment.expires_at) < new Date();

    return (expiredByUsage || expiredByDate) ? 'Expired' : 'Active';
  }

  if (!payment.expires_at) return 'Active';

  const expiryDate = new Date(payment.expires_at);

  if (isNaN(expiryDate.getTime())) return 'Active';

  return expiryDate > new Date() ? 'Active' : 'Expired';
};
  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));

  const formatINR = (value) =>
    Number(value || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

  const numberToIndianWords = (amount) => {
    const ones = [
      "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
      "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
      "Seventeen", "Eighteen", "Nineteen"
    ];

    const tens = [
      "", "", "Twenty", "Thirty", "Forty",
      "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"
    ];

    const convertBelowHundred = (num) => {
      if (num < 20) return ones[num];
      return `${tens[Math.floor(num / 10)]}${num % 10 ? " " + ones[num % 10] : ""}`;
    };

    const convertBelowThousand = (num) => {
      let result = "";

      if (num >= 100) {
        result += `${ones[Math.floor(num / 100)]} Hundred`;
        num %= 100;
        if (num) result += " ";
      }

      if (num > 0) {
        result += convertBelowHundred(num);
      }

      return result;
    };

    const convertInteger = (num) => {
      if (num === 0) return "Zero";

      let result = "";

      const crore = Math.floor(num / 10000000);
      num %= 10000000;

      const lakh = Math.floor(num / 100000);
      num %= 100000;

      const thousand = Math.floor(num / 1000);
      num %= 1000;

      const remainder = num;

      if (crore) {
        result += `${convertBelowThousand(crore)} Crore `;
      }

      if (lakh) {
        result += `${convertBelowThousand(lakh)} Lakh `;
      }

      if (thousand) {
        result += `${convertBelowThousand(thousand)} Thousand `;
      }

      if (remainder) {
        result += convertBelowThousand(remainder);
      }

      return result.trim();
    };

    const numericAmount = Number(amount || 0);
    const rupees = Math.floor(numericAmount);
    const paise = Math.round((numericAmount - rupees) * 100);

    let result = `Rupees ${convertInteger(rupees)}`;

    if (paise > 0) {
      result += ` and ${convertInteger(paise)} Paise`;
    }

    return `${result} Only`;
  };

  const handleDownloadReceipt = (payment) => {
    const hasGst = payment.tax_type && payment.tax_type !== 'NONE';
      const baseAmount = hasGst
        ? Number(payment.base_amount || 0) / 100
        : Number(payment.amount || 0) / 100;

      const totalGst = hasGst
        ? Number(payment.total_tax || 0) / 100
        : 0;

      const grandTotal = Number(payment.amount || 0) / 100;
      const amountInWords = numberToIndianWords(grandTotal);

      const gstRate =
        payment.tax_type === 'CGST_SGST'
          ? Number(payment.cgst_rate || 0) + Number(payment.sgst_rate || 0)
          : Number(payment.igst_rate || 0);

      const planDescription =
        planMap[payment.plan_code] || payment.plan_code || 'ExiHire Plan';

       const invoiceNumber =
         payment.invoice_number ||
         `INV-${new Date(payment.created_at).getFullYear()}-${String(payment.id || '')
           .replace(/-/g, '')
           .slice(0, 8)
           .toUpperCase()}`;

       const invoiceDate = new Date(payment.invoice_date || payment.created_at).toLocaleDateString('en-GB', {
         day: '2-digit',
         month: 'short',
         year: 'numeric'
       });

       const supplierName = payment.supplier_name || 'EVOLVE X INNOVATIONS';
       const supplierGstin = payment.supplier_gstin || '';
       const supplierPan = payment.supplier_pan || '';
       const supplierState = payment.supplier_state || 'Andhra Pradesh (37)';
       const supplierAddress = payment.supplier_address || 'Bhoomatha Thalluri Heights, Kankipadu, Vijayawada-521151';
       const supplierPhone = payment.supplier_phone || '+91 62815 97586';
       const supplierEmail = payment.supplier_email || 'connect@exihire.com';
       const supplierWebsite = payment.supplier_website || 'https://exihire.com';
       const supplierLogoUrl =
         payment.supplier_logo_url ||
         `${window.location.origin}/images/Evolve%20x%20logo.png`;
       const sacCode = '998315';


    // Basic receipt generation as a simple printable window/content
    const receiptContent = `
      <html>
        <head>
          <title>Payment Receipt - ${payment.razorpay_order_id || payment.id}</title>
          <style>
            html,
            body {
              width: 100%;
              margin: 0;
              padding: 0;
            }

            body {
              font-family: Arial, sans-serif;
              color: #333;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            .receipt-box {
              width: 100%;
              max-width: 100%;
              margin: 0;
              padding: 0 30px 30px;
              border: 1px solid #E5E7EB;
              border-radius: 0;
              box-sizing: border-box;
              overflow: hidden;
            }

            .invoice-header {
              background: #3F35B5;
              color: #FFFFFF;
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              padding: 22px 28px;
              margin: 0 -30px 22px;
              box-sizing: border-box;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            .invoice-company {
              display: flex;
              align-items: flex-start;
              gap: 16px;
              width: 68%;
            }

            .invoice-logo {
              width: 75px;
              height: 75px;
              object-fit: contain;
              flex-shrink: 0;
            }

            .company-details {
              flex: 1;
            }

            .company-name {
              margin: 1px 0 8px;
              font-size: 17px;
              line-height: 1.1;
              font-weight: 800;
              color: #FFFFFF;
            }

            .company-meta,
            .company-contact {
              margin: 0 0 5px;
              font-size: 8px;
              line-height: 1.45;
              color: #FFFFFF;
            }

            .invoice-heading-block {
              width: 30%;
              text-align: right;
            }

            .invoice-title {
              margin: 0 0 12px;
              font-size: 22px;
              line-height: 1;
              font-weight: 800;
              color: #FFFFFF;
            }

            .invoice-info {
              margin: 0 0 6px;
              font-size: 9px;
              line-height: 1.35;
              color: #FFFFFF;
            }
            .payment-details-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              column-gap: 24px;
              row-gap: 10px;
              margin-bottom: 6px;
            }
            .row { display: flex; justify-content: space-between; margin-bottom: 0; }
            .label { font-weight: bold; }
            .status-captured {
              color: green;
              font-weight: bold;
            }
            .invoice-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 24px;
              font-size: 11px;
            }

            .invoice-table th {
              background: #3F35B5;
              color: white;
              padding: 10px 6px;
              text-align: center;
              font-weight: 700;
              border: 1px solid #D1D5DB;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            .invoice-table th:first-child {
              text-align: left;
            }

            .invoice-table td {
              padding: 10px 6px;
              border: 1px solid #D1D5DB;
              text-align: right;
            }

            .invoice-table td:first-child {
              text-align: left;
            }

            .invoice-table .subtotal-row td {
              background: #F8FAFC;
              font-weight: 700;
            }

            .summary-section {
              display: flex;
              justify-content: flex-end;
              margin-top: 20px;
            }

            .gst-summary {
              width: 46%;
              border: 1px solid #D1D5DB;
              border-radius: 10px;
              overflow: hidden;
            }

            .gst-summary-title {
              padding: 10px 16px 6px;
              color: #4F46E5;
              font-weight: 700;
              font-size: 11px;
            }

            .gst-summary-divider {
              border: 0;
              border-top: 1px solid #D1D5DB;
              margin: 0 16px;
            }

            .gst-summary-row {
              display: flex;
              justify-content: space-between;
              padding: 8px 16px;
              font-size: 11px;
            }

            .grand-total-bar {
              display: flex;
              justify-content: space-between;
              background: #3F35B5;
              color: white;
              padding: 12px 16px;
              font-size: 12px;
              font-weight: 700;
              margin-top: 5px;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            .amount-words {
              margin-top: 24px;
              border: 1px solid #F4C542;
              background: #FFFBEA;
              border-radius: 10px;
              padding: 12px 16px;
              display: flex;
              gap: 25px;
              font-size: 11px;
            }

            .amount-words-label {
              color: #B45309;
              font-weight: 700;
              min-width: 120px;
            }

            .receipt-status {
              margin: 0 0 14px;
              display: flex;
              justify-content: flex-end;
              gap: 10px;
              font-size: 11px;
            }

            /* Force Terms & Conditions onto page 2 */
            .terms-page {
              break-before: page;
              page-break-before: always;
              padding-top: 20px;
            }

            .terms-page h2 {
              text-align: center;
              color: #1E40AF;
              margin: 0 0 20px;
              font-size: 22px;
            }

            .terms-content {
              font-size: 9px;
              line-height: 1.3;
              color: #333;
            }

            .term-item {
              margin-bottom: 5px;
            }

            .term-item strong {
              color: #111827;
            }

            .footer {
              margin-top: 25px;
              text-align: center;
              font-size: 10px;
              color: #777;
              border-top: 1px solid #eee;
              padding-top: 15px;
            }

            .print-footer {
              display: none;
            }

            @media print {
              @page {
                size: A4;
                margin: 15mm 12mm 22mm 12mm;
              }

              body {
                padding: 0;
                margin: 0;
              }

              .receipt-box {
                margin: 0 auto;
              }

              .terms-page {
                break-before: page;
                page-break-before: always;
              }

              .print-footer {
                display: block;
                position: fixed;
                left: 0;
                right: 0;
                bottom: 0;
                height: 18mm;
                background: #F8FAFC;
                border-top: 1px solid #E2E8F0;
                text-align: center;
                padding: 7px 10px 0;
                box-sizing: border-box;
                font-size: 8px;
                color: #475569;
              }

              .print-footer-line2 {
                margin-top: 5px;
                color: #1E40AF;
                font-weight: 700;
              }
            }
          </style>
        </head>
        <body>
          <div class="receipt-box">
            <div class="invoice-header">
              <div class="invoice-company">

                ${
                  supplierLogoUrl
                    ? `
                      <img
                        class="invoice-logo"
                        src="${escapeHtml(supplierLogoUrl)}"
                        alt="${escapeHtml(supplierName || 'Supplier')}"
                      />
                    `
                    : ''
                }

                <div class="company-details">
                  ${
                    supplierName
                      ? `<div class="company-name">${escapeHtml(supplierName)}</div>`
                      : ''
                  }

                  ${
                    supplierGstin || supplierPan || supplierState
                      ? `
                        <div class="company-meta">
                          ${supplierGstin ? `GSTIN: ${escapeHtml(supplierGstin)}` : ''}
                          ${supplierPan ? `${supplierGstin ? ' &nbsp; | &nbsp; ' : ''}PAN: ${escapeHtml(supplierPan)}` : ''}
                          ${supplierState ? `${supplierGstin || supplierPan ? ' &nbsp; | &nbsp; ' : ''}State: ${escapeHtml(supplierState)}` : ''}
                        </div>
                      `
                      : ''
                  }

                  ${
                    supplierAddress
                      ? `<div class="company-meta">${escapeHtml(supplierAddress)}</div>`
                      : ''
                  }

                  ${
                    supplierPhone || supplierEmail || supplierWebsite
                      ? `
                        <div class="company-contact">
                          ${supplierPhone ? escapeHtml(supplierPhone) : ''}
                          ${supplierEmail ? `${supplierPhone ? ' &nbsp; | &nbsp; ' : ''}${escapeHtml(supplierEmail)}` : ''}
                          ${supplierWebsite ? `${supplierPhone || supplierEmail ? ' &nbsp; | &nbsp; ' : ''}${escapeHtml(supplierWebsite)}` : ''}
                        </div>
                      `
                      : ''
                  }
                </div>
              </div>

              <div class="invoice-heading-block">
                <div class="invoice-title">TAX INVOICE</div>

                <div class="invoice-info">
                  Invoice No: ${escapeHtml(invoiceNumber)}
                </div>

                <div class="invoice-info">
                  Date: ${escapeHtml(invoiceDate)}
                </div>

                ${
                  sacCode
                    ? `
                      <div class="invoice-info">
                        SAC Code: ${escapeHtml(sacCode)}
                      </div>
                    `
                    : ''
                }
              </div>
            </div>

            <div class="receipt-status">
              <span class="label">Status:</span>
              <span class="status-captured">${payment.status.toUpperCase()}</span>
            </div>

            <div class="payment-details-grid">
              <div class="row">
                <span class="label">Date:</span>
                <span>${new Date(payment.created_at).toLocaleString()}</span>
              </div>
              <div class="row">
                <span class="label">Receipt No:</span>
                <span>${payment.id.substring(0, 8).toUpperCase()}</span>
              </div>
              <div class="row">
                <span class="label">Order ID:</span>
                <span>${payment.razorpay_order_id || 'N/A'}</span>
              </div>
              <div class="row">
                <span class="label">Payment ID:</span>
                <span>${payment.payment_id || 'N/A'}</span>
              </div>
            </div>
            <hr />

            <table class="invoice-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>SAC Code</th>
                  <th>Qty</th>
                  <th>Unit Price (Rs.)</th>
                  <th>Taxable Value (Rs.)</th>
                  <th>GST %</th>
                  <th>GST Amt (Rs.)</th>
                  <th>Total (Rs.)</th>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td>ExiHire ${escapeHtml(planDescription)} Plan</td>
                  <td>${escapeHtml(sacCode || 'N/A')}</td>
                  <td>1</td>
                  <td>${formatINR(baseAmount)}</td>
                  <td>${formatINR(baseAmount)}</td>
                  <td>${hasGst ? gstRate.toFixed(0) : '0'}%</td>
                  <td>${formatINR(totalGst)}</td>
                  <td>${formatINR(grandTotal)}</td>
                </tr>

                <tr class="subtotal-row">
                  <td colspan="4">Subtotal</td>
                  <td>${formatINR(baseAmount)}</td>
                  <td></td>
                  <td>${formatINR(totalGst)}</td>
                  <td>${formatINR(grandTotal)}</td>
                </tr>
              </tbody>
            </table>

            <div class="summary-section">
              <div class="gst-summary">
                <div class="gst-summary-title">GST SUMMARY</div>
                <hr class="gst-summary-divider" />

                <div class="gst-summary-row">
                  <span>Taxable Value</span>
                  <strong>Rs. ${formatINR(baseAmount)}</strong>
                </div>

                ${
                  payment.tax_type === 'CGST_SGST'
                    ? `
                      <div class="gst-summary-row">
                        <span>CGST @ ${payment.cgst_rate}%</span>
                        <strong>Rs. ${formatINR(Number(payment.cgst_amount || 0) / 100)}</strong>
                      </div>

                      <div class="gst-summary-row">
                        <span>SGST @ ${payment.sgst_rate}%</span>
                        <strong>Rs. ${formatINR(Number(payment.sgst_amount || 0) / 100)}</strong>
                      </div>
                    `
                    : hasGst
                    ? `
                      <div class="gst-summary-row">
                        <span>IGST @ ${payment.igst_rate}%</span>
                        <strong>Rs. ${formatINR(Number(payment.igst_amount || 0) / 100)}</strong>
                      </div>
                    `
                    : `
                      <div class="gst-summary-row">
                        <span>GST</span>
                        <strong>Rs. 0.00</strong>
                      </div>
                    `
                }

                <div class="grand-total-bar">
                  <span>Grand Total</span>
                  <span>Rs. ${formatINR(grandTotal)}</span>
                </div>
              </div>
            </div>

            <div class="amount-words">
              <span class="amount-words-label">Amount in Words:</span>
              <span>${escapeHtml(amountInWords)}</span>
            </div>

            </div>

            <div class="terms-page">
                        <h2>Terms &amp; Conditions</h2>

                        <div class="terms-content">
                          <div class="term-item"><strong>1. Taxes:</strong> Prices are exclusive of GST unless explicitly stated otherwise. GST shall be charged as applicable under Indian GST laws.</div>

                          <div class="term-item"><strong>2. Activation of Services:</strong> Services, subscriptions and purchased credits are activated only after successful payment confirmation.</div>

                          <div class="term-item"><strong>3. SaaS Platform:</strong> ExiHire is a cloud-based Software-as-a-Service recruitment and hiring platform.</div>

                          <div class="term-item"><strong>4. Subscription Scope:</strong> Access to job postings, applications, AI shortlisting, AI interviews, assessments, communications, reports and analytics is governed by the purchased plan.</div>

                          <div class="term-item"><strong>5. Plan Limits and Usage:</strong> Job, application, AI interview, assessment, calling, WhatsApp and other usage limits vary by plan or commercial agreement.</div>

                          <div class="term-item"><strong>6. Non-Refundable Payments:</strong> All subscription fees, credits, implementation charges, onboarding fees, setup fees and other payments are non-refundable and non-transferable except where required by law.</div>

                          <div class="term-item"><strong>7. No Cancellation After Payment:</strong> No cancellation is permitted after successful payment confirmation and activation.</div>

                          <div class="term-item"><strong>8. No Transfer or Exchange:</strong> Purchased subscriptions, licenses, credits and other entitlements cannot be exchanged for cash or transferred unless approved in writing.</div>

                          <div class="term-item"><strong>9. Expiry of Unused Entitlements:</strong> Unused credits and remaining entitlements expire according to the applicable plan or commercial agreement.</div>

                          <div class="term-item"><strong>10. Customer Billing Information:</strong> Customers are responsible for providing accurate legal entity, GSTIN, billing address and contact information.</div>

                          <div class="term-item"><strong>11. Refund &amp; Cancellation Policy:</strong> All payments are final. No refunds, cancellations or transfers are permitted after successful payment except where required by law.</div>

                          <div class="term-item"><strong>12. AI-Powered Features:</strong> AI-generated outputs support recruitment decisions and must not be treated as the sole basis for final high-impact hiring decisions.</div>

                          <div class="term-item"><strong>13. Customer Responsibility for Hiring Decisions:</strong> Customers remain responsible for candidate review, verification, due diligence and final hiring decisions.</div>

                          <div class="term-item"><strong>14. Accuracy of AI Outputs:</strong> AI-generated questions, scores, evaluations, rankings and recommendations are not guaranteed to be complete, accurate or error-free.</div>

                          <div class="term-item"><strong>15. Candidate Data and Lawful Use:</strong> Customers are responsible for ensuring lawful handling of candidate and personal data.</div>

                          <div class="term-item"><strong>16. Communication Services:</strong> Email, SMS, WhatsApp and voice services may depend on third-party providers and delivery is not guaranteed.</div>

                          <div class="term-item"><strong>17. Third-Party Services:</strong> Certain features depend on payment gateways, cloud providers, telecom providers, messaging platforms and AI providers.</div>

                          <div class="term-item"><strong>18. Acceptable Use:</strong> ExiHire must not be used for unlawful recruitment, fraud, spam, harassment, unauthorized data collection or platform abuse.</div>

                          <div class="term-item"><strong>19. Account Security:</strong> Customers are responsible for maintaining account credential and access security.</div>

                          <div class="term-item"><strong>20. Service Availability:</strong> Uninterrupted or error-free platform access is not guaranteed.</div>

                          <div class="term-item"><strong>21. Technical Support:</strong> Support is provided according to subscribed plan entitlements and applicable commercial agreements.</div>

                          <div class="term-item"><strong>22. Plan and Feature Changes:</strong> EVOLVE X INNOVATIONS may update platform features, limits, integrations and service offerings.</div>

                          <div class="term-item"><strong>23. Suspension or Termination for Misuse:</strong> Access may be suspended for fraud, unlawful activity, security risks, payment default or misuse.</div>

                          <div class="term-item"><strong>24. Statutory Charges:</strong> Applicable statutory taxes, duties, levies or government charges shall be payable by the customer.</div>

                          <div class="term-item"><strong>25. Limitation of Liability:</strong> EVOLVE X INNOVATIONS shall not be liable for indirect, incidental, consequential or business losses to the maximum extent permitted by law.</div>

                          <div class="term-item"><strong>26. No Employment Guarantee:</strong> Use of ExiHire does not guarantee successful hiring, candidate availability, retention or specific business results.</div>

                          <div class="term-item"><strong>27. Electronic Invoice:</strong> Electronically issued invoices are computer-generated documents and do not require a physical signature, subject to applicable law.</div>

                          <div class="term-item"><strong>28. Governing Law and Jurisdiction:</strong> Transactions are governed by Indian law and subject to competent courts at Vijayawada, Andhra Pradesh, India.</div>

                          <div class="term-item"><strong>29. Acceptance of Terms:</strong> By purchasing or using ExiHire, the customer acknowledges acceptance of applicable Terms &amp; Conditions, plan details, policies and agreements.</div>
                        </div>

                        <div class="footer">
                          <p>Thank you for your business!</p>
                          <p>This is a computer generated receipt and does not require a physical signature.</p>
                          <p>Contact: connect@exihire.com</p>
                        </div>

                        </div>

                        <div class="print-footer">
                          <div>
                            This is a computer-generated ExiHire receipt. Subject to Vijayawada jurisdiction.
                          </div>

                          <div class="print-footer-line2">
                            connect@exihire.com
                            &nbsp; | &nbsp;
                            +91 62815 97586
                            &nbsp; | &nbsp;
                            https://exihire.com
                          </div>
                        </div>

                        <script>
                          window.onload = function() { window.print(); }
                        </script>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(receiptContent);
    printWindow.document.close();
  };

  const isSuperAdminView = window.location.pathname.includes('super-admin');

  const filteredPayments = payments.filter(payment => {
    const userName = `${payment.first_name || ''} ${payment.last_name || ''}`.toLowerCase();
    const email = (payment.email || '').toLowerCase();
    const orderId = (payment.razorpay_order_id || '').toLowerCase();
    const matchesSearch = userName.includes(searchTerm.toLowerCase()) || 
                          email.includes(searchTerm.toLowerCase()) ||
                          orderId.includes(searchTerm.toLowerCase());

    const planDisplayName = planMap[payment.plan_code] || payment.plan_code || '';
    const matchesFilter = planFilter === 'All' || planDisplayName === planFilter;

    return matchesSearch && matchesFilter;
  });

  const planOptions = Array.from(new Set([
    'All',
    'Free Trial',
    'Pay As You Go',
    'Starter',
    'Annual',
    'Overage',
    ...payments.map(p => planMap[p.plan_code] || p.plan_code).filter(Boolean)
  ]));

  const paginatedPayments = filteredPayments.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );
  const totalPages = Math.ceil(filteredPayments.length / rowsPerPage);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              color: "#1E3A8A",
              fontWeight: "bold",
              fontSize: { xs: "1.5rem", md: "2rem" }
            }}
          >
            Payment History
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mt: 0.5
            }}
          >
            View your payment history and download transaction receipts.
          </Typography>
        </Box>

        {isSuperAdminView && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search by User"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 300 }}
            />
            <FormControl size="small">
              <Select
                value={planFilter}
                onChange={(e) => {
                  setPlanFilter(e.target.value);
                  setPage(1);
                }}
                displayEmpty
                renderValue={() => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#1E3A8A', fontWeight: 'bold' }}>
                    <FilterList sx={{ fontSize: 20 }} />
                    Filters
                  </Box>
                )}
                sx={{
                  borderRadius: isSuperAdminView ? 0 : '8px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1E3A8A',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1E40AF',
                  },
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                  }
                }}
              >
                <MenuItem disabled sx={{ fontWeight: 'bold', color: '#1E3A8A', borderBottom: '1px solid #eee', justifyContent: 'center', opacity: '1 !important' }}>
                  Filters
                </MenuItem>
                {planOptions.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>
      <TableContainer component={Paper} sx={{ mt: 2, borderRadius: isSuperAdminView ? 0 : 2, boxShadow: 3 }}>
        <Table>
          <TableHead sx={{ bgcolor: 'f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{window.location.pathname.includes('super-admin') ? 'User / Details' : 'Order ID'}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Invoice No.</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Plan</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
          {paginatedPayments.map((payment) => {
            const displayStatus = getDisplayStatus(payment);

            return (
              <TableRow key={payment.id} hover>
                <TableCell>{new Date(payment.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  {payment.first_name ? `${payment.first_name} ${payment.last_name}` : (payment.razorpay_order_id || 'N/A')}
                  {payment.email && <Typography variant="caption" display="block" color="text.secondary">{payment.email}</Typography>}
                </TableCell>
                <TableCell>{payment.invoice_number || 'N/A'}</TableCell>
                <TableCell>{planMap[payment.plan_code] || payment.plan_code}</TableCell>
                <TableCell>₹{payment.amount / 100}</TableCell>
                <TableCell>
                  <Chip
                    label={displayStatus}
                    color={displayStatus === 'Active' ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <Button
                    startIcon={<Download />}
                    size="small"
                    variant="outlined"
                    onClick={() => handleDownloadReceipt(payment)}
                    disabled={!['captured', 'expired'].includes(payment.status)}
                  >
                    Receipt
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
            {paginatedPayments.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No payment records found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {paginatedPayments.length > 0 ? (page - 1) * rowsPerPage + 1 : 0} to{" "}
          {Math.min(page * rowsPerPage, filteredPayments.length)} of{" "}
          {filteredPayments.length} records
        </Typography>
        <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            shape="rounded"
            color="primary"
            sx={{
              "& .MuiPaginationItem-root.Mui-selected": {
                backgroundColor: "#1E3A8A",
                "&:hover": {
                  backgroundColor: "#1E40AF",
                },
              },
            }}
        />
      </Box>
    </Container>
  );
}

export default Payment;
